import React from 'react';
import { Calendar, MapPin, Clock, Users, Award, Target, Star } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';

const agenda = [
  { time: '09:00 – 09:15', type: 'registration', title: 'Registration & Welcome Coffee', detail: null },
  {
    time: '09:15 – 09:30', type: 'welcome', title: 'Welcome Remarks',
    detail: { speaker: 'Seoul Women\'s University (TBC)', note: 'Opening the forum with a vision for women\'s leadership, ethical innovation, and global collaboration in AI.' }
  },
  {
    time: '09:30 – 09:50', type: 'keynote', title: 'Opening Keynote',
    detail: {
      speaker: 'Lee Kyoung Sook',
      speakerTitle: 'Former Chancellor, Sookmyung Women\'s University',
      theme: '"Give to Gain: Leadership, Inclusion, and Responsible AI in a Transforming World"'
    }
  },
  {
    time: '09:50 – 10:30', type: 'keynote', title: 'Keynote Presentations (2 × 20 min)',
    detail: {
      keynotes: [
        { label: 'Keynote 1', title: 'Human-Centred AI & Social Innovation', points: ['AI for public good and inclusive growth', 'Ethics, trust, and societal impact'] },
        { label: 'Keynote 2', title: 'AI for Industry & Business Transformation', points: ['Responsible AI adoption at scale', 'Balancing innovation, performance, and accountability'] },
      ]
    }
  },
  { time: '10:30 – 10:50', type: 'break', title: 'Tea & Coffee Break', detail: null },
  {
    time: '10:50 – 11:50', type: 'panel', title: 'Panel Discussions (3 Panels)',
    detail: {
      panels: [
        { label: 'Panel 1', title: 'Human-Centred & Ethical AI Leadership', points: ['Embedding ethics and responsibility in AI systems', 'Governance, transparency, and trust'] },
        { label: 'Panel 2', title: 'Women Leading AI for Industry & Business Impact', points: ['Executive leadership journeys', 'AI strategy, platforms, and enterprise transformation'] },
        { label: 'Panel 3', title: 'AI for Social Innovation & Biotechnology', points: ['AI in healthcare, biotech, and life sciences', 'Responsible innovation and societal outcomes'] },
      ]
    }
  },
  {
    time: '11:50 – 12:10', type: 'mou', title: 'MoU Signing Ceremony',
    detail: {
      group: 'APAC AI Advisory Group',
      points: ['Formalisation of regional collaboration', 'Founding advisory members from academia, industry, and policy', 'Commitment to advancing responsible AI leadership across APAC']
    }
  },
  {
    time: '12:10 – 12:40', type: 'awards', title: 'AI4HER APAC Ada Lovelace IWD Awards 2026',
    detail: {
      awards: [
        { category: 'Ada Lovelace Lifetime Achievement Award 2026', recipient: 'Lee Kyoung Sook', recipientTitle: 'Former Chancellor, Sookmyung Women\'s University' },
        { category: 'Ada Lovelace Scholar Leadership Award 2026', recipient: 'Seoul Women\'s University (TBC)', recipientTitle: '' },
        { category: 'Ada Lovelace AI Leadership Award 2026', recipient: 'Executive Director, Databricks', recipientTitle: '' },
        { category: 'Ada Lovelace AI for Biotech Award 2026', recipient: 'Chief AI Institute, Drug Development', recipientTitle: '' },
        { category: 'Ada Lovelace AI Innovation Award 2026', recipient: 'Director, OpenAI', recipientTitle: '' },
      ]
    }
  },
  {
    time: '12:40 – 12:50', type: 'closing', title: 'Closing Remarks',
    detail: { points: ['Reflections on "Give to Gain"', 'Call to action for inclusive, ethical AI leadership across APAC'] }
  },
  { time: '12:50 – 13:00', type: 'networking', title: 'Photo Session & Networking', detail: null },
];

const objectives = [
  'Promote Human-Centred, Ethical & Responsible AI across APAC',
  'Showcase women leaders driving AI for social impact and business transformation',
  'Strengthen collaboration between universities, industry, startups, and policy leaders',
  'Advance diversity, equity, and inclusion in AI leadership',
  'Formalise regional cooperation through the APAC AI Advisory Group MoU',
];

const typeStyle: Record<string, string> = {
  registration: 'bg-gray-100 text-gray-600',
  welcome:      'bg-blue-100 text-blue-700',
  keynote:      'bg-teal-50 text-teal-700',
  panel:        'bg-purple-100 text-purple-700',
  break:        'bg-amber-50 text-amber-600',
  mou:          'bg-teal-100 text-teal-700',
  awards:       'bg-yellow-100 text-yellow-700',
  closing:      'bg-pink-100 text-pink-700',
  networking:   'bg-green-100 text-green-700',
};

const typeLabel: Record<string, string> = {
  registration: '등록',
  welcome:      '환영사',
  keynote:      '기조연설',
  panel:        '패널토론',
  break:        '휴식',
  mou:          'MoU 서명',
  awards:       '시상식',
  closing:      '폐회사',
  networking:   '네트워킹',
};

const AI4HERIWD2026Page: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="h-16" />
      <Breadcrumb />

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-navy-950 via-navy-900 to-teal-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #c084fc 0%, transparent 50%), radial-gradient(circle at 80% 20%, #f472b6 0%, transparent 50%)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="bg-pink-500/30 border border-pink-300/50 text-pink-200 text-xs font-bold px-4 py-1.5 rounded-full">
              🌸 International Women's Day 2026
            </span>
            <span className="bg-white/10 border border-white/20 text-gray-300 text-xs font-medium px-4 py-1.5 rounded-full">
              AI4HER APAC × 서울여자대학교
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-3 leading-tight">
            AI Ethics &amp;<br />
            <span className="bg-gradient-to-r from-pink-300 to-purple-200 bg-clip-text text-transparent">
              Women's Leadership Forum
            </span>
          </h1>
          <p className="text-xl text-purple-200 italic mb-10 font-light">Theme: "Give to Gain"</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl">
            {[
              { icon: Calendar, label: '일시', value: '2026년 3월 27일 (금)' },
              { icon: Clock,    label: '시간', value: '09:00 – 13:00' },
              { icon: MapPin,   label: '장소', value: '50주년기념관 국제회의실' },
              { icon: Users,    label: '형식', value: 'In-person (초청 리더 중심)' },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <Icon className="h-5 w-5 text-pink-300 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-purple-300 font-medium">{label}</p>
                  <p className="text-sm text-white font-semibold">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-14">

        {/* Summary */}
        <section className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="h-6 w-6 text-teal-600" /> Executive Summary
          </h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            The International Women's Day 2026 – AI Ethics & Women's Leadership Forum convenes distinguished women leaders from academia, AI industry, biotechnology, business, and public institutions to advance dialogue and action on human-centred, ethical, and responsible artificial intelligence.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Aligned with the 2026 theme <strong className="text-gray-800">"Give to Gain"</strong>, the forum emphasises leadership that delivers shared value — where innovation, inclusion, and social impact drive sustainable growth for industry and society alike.
          </p>
        </section>

        {/* Objectives */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Star className="h-6 w-6 text-teal-600" /> Forum Objectives
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {objectives.map((obj, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <p className="text-sm text-gray-700 leading-relaxed">{obj}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Agenda */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-teal-600" /> 프로그램 일정
          </h2>
          <p className="text-sm text-gray-500 mb-8">27 March 2026 &nbsp;|&nbsp; 09:00 AM – 1:00 PM &nbsp;|&nbsp; 서울여자대학교 50주년기념관 국제회의실</p>

          <div className="space-y-3">
            {agenda.map((item, idx) => (
              <div key={idx} className={`rounded-xl overflow-hidden border border-gray-100 shadow-sm ${item.type === 'break' ? 'opacity-70' : ''}`}>
                <div className="flex items-start gap-4 p-5 bg-white">
                  {/* 시간 */}
                  <div className="flex-shrink-0 w-28 text-xs font-mono text-gray-500 pt-0.5">{item.time}</div>

                  {/* 배지 */}
                  <div className="flex-shrink-0">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeStyle[item.type]}`}>
                      {typeLabel[item.type]}
                    </span>
                  </div>

                  {/* 내용 */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 mb-1">{item.title}</p>

                    {item.detail && (
                      <>
                        {/* welcome / closing */}
                        {'speaker' in item.detail && !('keynotes' in item.detail) && !('awards' in item.detail) && !('panels' in item.detail) && !('group' in item.detail) && (
                          <div className="text-sm text-gray-600">
                            <p className="font-medium text-gray-800">{(item.detail as any).speaker}</p>
                            {(item.detail as any).speakerTitle && <p className="text-xs text-gray-500">{(item.detail as any).speakerTitle}</p>}
                            {(item.detail as any).theme && <p className="mt-1 italic text-teal-600 text-xs">{(item.detail as any).theme}</p>}
                            {(item.detail as any).note && <p className="mt-1 text-xs text-gray-500">{(item.detail as any).note}</p>}
                            {(item.detail as any).points && (
                              <ul className="mt-2 space-y-1">
                                {(item.detail as any).points.map((p: string, i: number) => (
                                  <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                                    <span className="text-teal-400 mt-0.5">•</span>{p}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}

                        {/* keynotes list */}
                        {'keynotes' in item.detail && (
                          <div className="grid sm:grid-cols-2 gap-3 mt-2">
                            {(item.detail as any).keynotes.map((k: any, i: number) => (
                              <div key={i} className="bg-teal-50/60 rounded-lg p-3">
                                <p className="text-xs font-bold text-teal-600 mb-0.5">{k.label}</p>
                                <p className="text-sm font-semibold text-gray-800 mb-1">{k.title}</p>
                                {k.points.map((p: string, j: number) => (
                                  <p key={j} className="text-xs text-gray-500">• {p}</p>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* panels list */}
                        {'panels' in item.detail && (
                          <div className="grid sm:grid-cols-3 gap-3 mt-2">
                            {(item.detail as any).panels.map((p: any, i: number) => (
                              <div key={i} className="bg-purple-50 rounded-lg p-3">
                                <p className="text-xs font-bold text-purple-600 mb-0.5">{p.label}</p>
                                <p className="text-sm font-semibold text-gray-800 mb-1">{p.title}</p>
                                {p.points.map((pt: string, j: number) => (
                                  <p key={j} className="text-xs text-gray-500">• {pt}</p>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* MoU */}
                        {'group' in item.detail && (
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-teal-700 mb-1">{(item.detail as any).group} — AI4HER Human-Centred, Ethical & Responsible AI</p>
                            <ul className="space-y-1">
                              {(item.detail as any).points.map((p: string, i: number) => (
                                <li key={i} className="text-xs text-gray-500 flex items-start gap-1.5">
                                  <span className="text-teal-400 mt-0.5">•</span>{p}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Awards */}
                        {'awards' in item.detail && (
                          <div className="mt-3 space-y-2">
                            {(item.detail as any).awards.map((a: any, i: number) => (
                              <div key={i} className="flex items-start gap-3 bg-yellow-50 rounded-lg px-4 py-2.5">
                                <span className="text-lg">🏆</span>
                                <div>
                                  <p className="text-xs font-bold text-yellow-700">AI4HER APAC {a.category}</p>
                                  <p className="text-sm text-gray-800 font-medium">{a.recipient}</p>
                                  {a.recipientTitle && <p className="text-xs text-gray-500">{a.recipientTitle}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Outcomes */}
        <section className="bg-teal-50/60 rounded-2xl p-8 border border-teal-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Award className="h-6 w-6 text-teal-600" /> Expected Outcomes & Legacy
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Strengthened APAC collaboration on responsible AI leadership',
              'Increased visibility of women driving ethical AI innovation',
              'Formal establishment of the APAC AI Advisory Group',
              'Inspiration and pathways for future women leaders in AI',
              'Long-term partnership between AI4HER APAC and Seoul Women\'s University',
              'Post-event impact report and media amplification',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-teal-500 mt-0.5">✓</span>
                {item}
              </div>
            ))}
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default AI4HERIWD2026Page;
